import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import { Link, useNavigate } from 'react-router-dom';
import { Book, Search, Filter, Sparkles, PlayCircle, Pin } from 'lucide-react';
import { getAllCourses, getAllUserCourseProgress, getCourseContentActivity } from '../services/firebase/courses';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import KpiTile from '../components/finance/KpiTile';
import Modal from '../components/Modal';
import PortalBackground from '../components/PortalBackground';

const formatTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = typeof date === 'number' ? new Date(date) : new Date(date);
  const diffInSeconds = Math.floor((now - d) / 1000);
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getCategoryColor = (category) => {
  switch (category) {
    case 'Sales':
      return 'bg-red-600';
    case 'Operations':
      return 'bg-blue-600';
    case 'Technology':
      return 'bg-purple-600';
    case 'Finance':
      return 'bg-green-600';
    case 'Marketing':
      return 'bg-orange-500';
    case 'HR':
      return 'bg-yellow-500';
    default:
      return 'bg-gray-600';
  }
};

const getProgressColor = (progress) => {
  if (progress >= 75) return 'bg-emerald-500';
  if (progress >= 50) return 'bg-amber-500';
  if (progress >= 25) return 'bg-orange-500';
  return 'bg-red-500';
};

const sectionCardClass =
  'rounded-xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm p-5 mb-6';

// Courses with content added/updated within this window float to the top of the grid.
const NEW_CONTENT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const CourseCard = ({ title, description, thumbnailUrl, category, progress = 0, path, isNew = false, isPinned = false }) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  return (
    <div
      onClick={() => navigate(path)}
      className={`rounded-xl border bg-white dark:bg-gray-900 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col h-full cursor-pointer relative ${
        isPinned ? 'border-amber-400 ring-2 ring-amber-400' : 'border-gray-200 dark:border-gray-800'
      }`}
    >
      <div className={`absolute top-4 left-4 px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white ${getCategoryColor(category)}`}>
        {category}
      </div>
      {(isPinned || isNew) && (
        <div className="absolute top-4 right-4 flex items-center gap-1.5">
          {isNew && (
            <div className="px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white bg-red-600 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" />
              New
            </div>
          )}
          {isPinned && (
            <div className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold text-amber-950 bg-gradient-to-r from-amber-300 to-yellow-500 flex items-center gap-1">
              <Pin className="w-2.5 h-2.5 fill-current" />
              Pinned
            </div>
          )}
        </div>
      )}

      {thumbnailUrl ? (
        <div className="mb-4 mt-6">
          <img src={thumbnailUrl} alt={title} className="w-full h-40 object-cover rounded-lg" />
        </div>
      ) : (
        <div className="mb-4 mt-6 flex justify-center">
          <div className="w-full h-32 rounded-lg bg-gray-100 dark:bg-gray-700/50 flex items-center justify-center">
            <Book className="w-10 h-10 text-red-600 opacity-60" />
          </div>
        </div>
      )}
      <div className="mb-3">
        <h3 className={`font-semibold text-lg mb-1 line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{title}</h3>
        <p className={`text-sm line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>
      </div>
      <div className="mt-auto">
        <div className={`w-full rounded-full h-1.5 overflow-hidden mb-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div
            className={`${getProgressColor(progress)} h-1.5 rounded-full transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{progress}% complete</span>
      </div>
    </div>
  );
};

const CompactFeedCard = ({ item }) => {
  const { darkMode } = useTheme();
  const isCourse = item.kind === 'course';
  const pillLabel = isCourse ? 'New Course' : 'New Lesson';
  const dateLabel = `Added ${formatTimeAgo(item.sortDate)}`;

  return (
    <Link
      to={item.path}
      className={`shrink-0 w-56 rounded-lg border p-3 transition-shadow hover:shadow-md ${
        darkMode ? 'bg-gray-700/50 border-gray-600 hover:border-red-600' : 'bg-gray-50 border-gray-200 hover:border-red-200'
      }`}
    >
      {isCourse && item.thumbnailUrl ? (
        <img src={item.thumbnailUrl} alt={item.title} className="w-full h-24 object-cover rounded-md mb-2" />
      ) : (
        <div className="relative w-full h-24 rounded-md bg-gray-200 dark:bg-gray-600 overflow-hidden flex items-center justify-center mb-2">
          {item.thumbnailUrl && (
            <img
              src={item.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-20"
            />
          )}
          {isCourse ? (
            <Book className="relative z-10 w-8 h-8 text-red-600" />
          ) : (
            <PlayCircle className="relative z-10 w-8 h-8 text-red-600" />
          )}
        </div>
      )}
      <span
        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mb-1.5 ${
          isCourse
            ? 'bg-red-600 text-white'
            : 'bg-gray-800 text-white dark:bg-gray-600'
        }`}
      >
        {pillLabel}
      </span>
      <h3 className={`text-sm font-semibold line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{item.title}</h3>
      {!isCourse && item.courseTitle && (
        <p className={`text-xs line-clamp-1 mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.courseTitle}</p>
      )}
      {isCourse && item.category && (
        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${getCategoryColor(item.category)}`}>
          {item.category}
        </span>
      )}
      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{dateLabel}</p>
    </Link>
  );
};

const CourseDashboard = () => {
  const { darkMode } = useTheme();
  const { isMobile } = useResponsive();
  const { currentUser: user, loading: authLoading } = useAuth();
  const [courses, setCourses] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [recentFeed, setRecentFeed] = useState([]);
  const [latestActivityByCourse, setLatestActivityByCourse] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [showNewModal, setShowNewModal] = useState(false);

  const departments = ['All', ...Array.from(new Set(courses.map((course) => course.category || 'General').filter(Boolean)))];

  const newContentCutoff = Date.now() - NEW_CONTENT_WINDOW_MS;

  const filteredCourses = courses
    .filter((course) => {
      const matchesSearch =
        searchTerm === '' ||
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (course.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = selectedDepartment === 'All' || course.category === selectedDepartment;
      return matchesSearch && matchesDepartment;
    })
    // Pinned courses first, then courses with new content (newest activity on top); the rest keep their manual order.
    .sort((a, b) => {
      if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
      const aDate = latestActivityByCourse[a.id] || 0;
      const bDate = latestActivityByCourse[b.id] || 0;
      const aNew = aDate >= newContentCutoff;
      const bNew = bDate >= newContentCutoff;
      if (aNew && bNew) return bDate - aDate;
      if (aNew) return -1;
      if (bNew) return 1;
      return 0;
    });

  const kpiStats = useMemo(() => {
    const total = courses.length;
    let inProgress = 0;
    let completed = 0;

    courses.forEach((course) => {
      const pct = userProgress[course.id]?.progressPercentage ?? 0;
      if (pct === 100) completed += 1;
      else if (pct > 0) inProgress += 1;
    });

    return { total, inProgress, completed };
  }, [courses, userProgress]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [fetchedCourses, activity] = await Promise.all([
          getAllCourses(),
          getCourseContentActivity().catch(() => ({ items: [], latestByCourse: {} }))
        ]);

        const publishedCourses = fetchedCourses.filter((course) => course.isPublished);
        setCourses(publishedCourses);
        setRecentFeed(activity.items.slice(0, 4));
        setLatestActivityByCourse(activity.latestByCourse);

        if (user?.uid) {
          try {
            const progress = await getAllUserCourseProgress(user.uid);
            setUserProgress(progress);
          } catch (progressError) {
            console.error('Error fetching user progress:', progressError);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('Failed to load courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchData();
    }

    const handleLessonProgressUpdate = async () => {
      if (user?.uid) {
        try {
          const updatedProgress = await getAllUserCourseProgress(user.uid);
          setUserProgress(updatedProgress);
        } catch (progressErr) {
          console.error('Error refreshing progress after lesson update:', progressErr);
        }
      }
    };

    window.addEventListener('lessonProgressUpdated', handleLessonProgressUpdate);
    return () => window.removeEventListener('lessonProgressUpdated', handleLessonProgressUpdate);
  }, [user?.uid, authLoading]);

  if (authLoading) {
    return (
      <div className={`p-6 md:p-8 flex items-center justify-center min-h-[40vh] ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <BBALoader size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 md:p-8 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <div className="text-center py-12">
          <Book size={48} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-medium mb-2">Error loading courses</h3>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen p-6 md:p-8 text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative">
      <div className="text-center mb-6">
        <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Courses</h1>
      </div>

      <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-3 mb-6`}>
        <KpiTile label="Total" value={String(kpiStats.total)} status="none" source="manual" showSourceBadge={false} icon={<span className="text-base leading-none">🎓</span>} loading={loading} />
        <KpiTile label="In Progress" value={String(kpiStats.inProgress)} status="none" source="manual" showSourceBadge={false} icon={<span className="text-base leading-none">▶️</span>} loading={loading} />
        <KpiTile label="Completed" value={String(kpiStats.completed)} status="none" source="manual" showSourceBadge={false} icon={<span className="text-base leading-none">✅</span>} loading={loading} />
        <KpiTile
          label="New Courses & Lessons"
          value={String(recentFeed.length)}
          status="none"
          source="manual"
          showSourceBadge={false}
          icon={<span className="text-base leading-none">✨</span>}
          loading={loading}
          onClick={recentFeed.length > 0 ? () => setShowNewModal(true) : undefined}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 mb-6">
          <BBALoader size={40} />
        </div>
      ) : (
        <>
          <div className={`${sectionCardClass} !py-4`}>
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-red-500 focus:ring-red-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:ring-red-500'
                  } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className={`h-4 w-4 shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className={`px-3 py-2.5 rounded-lg border text-sm min-w-[140px] ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white focus:border-red-500 focus:ring-red-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-red-500 focus:ring-red-500'
                  } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredCourses.map((course) => {
              const courseProgress = userProgress[course.id];
              const progress = courseProgress ? courseProgress.progressPercentage : 0;

              return (
                <CourseCard
                  key={course.id}
                  title={course.title}
                  description={course.description}
                  thumbnailUrl={course.thumbnailUrl}
                  category={course.category || 'General'}
                  progress={progress}
                  path={`/portal/courses/${course.id}`}
                  isNew={(latestActivityByCourse[course.id] || 0) >= newContentCutoff}
                  isPinned={!!course.isPinned}
                />
              );
            })}
          </div>

          {filteredCourses.length === 0 && (
            <div className="text-center py-12">
              <Book size={48} className={`mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No courses found</h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Try adjusting your search terms or department filter.</p>
            </div>
          )}
        </>
      )}

      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} maxWidth="xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xl leading-none" aria-hidden="true">✨</span>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">New Courses & Lessons</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 justify-items-center">
          {recentFeed.map((item) => (
            <CompactFeedCard
              key={item.kind === 'course' ? `course-${item.courseId}` : `lesson-${item.courseId}-${item.lessonId}`}
              item={item}
            />
          ))}
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default CourseDashboard;
